import { cn } from "@/lib/cn";

export const GAUGE_MAX_SCALE = 130;
const THRESHOLDS = { green: 99, yellow: 60 } as const;

const RED_PCT = (THRESHOLDS.yellow / GAUGE_MAX_SCALE) * 100;
const YELLOW_PCT = ((THRESHOLDS.green - THRESHOLDS.yellow) / GAUGE_MAX_SCALE) * 100;
const GREEN_PCT = ((GAUGE_MAX_SCALE - THRESHOLDS.green) / GAUGE_MAX_SCALE) * 100;

interface TemperatureGaugeProps {
  percentage: number;
  orientation?: "horizontal" | "vertical";
  size?: "sm" | "md" | "lg" | "xl";
  className?: string;
}

function clampMarkerPct(percentage: number): number {
  const clamped = Math.min(Math.max(percentage, 0), GAUGE_MAX_SCALE);
  return (clamped / GAUGE_MAX_SCALE) * 100;
}

const SIZE_CLASS = {
  horizontal: {
    sm: "h-4",
    md: "h-5",
    lg: "h-6",
    xl: "h-8",
  },
  vertical: {
    sm: "w-4",
    md: "w-5",
    lg: "w-6",
    xl: "w-8",
  },
} as const;

export function TemperatureGauge({
  percentage,
  orientation = "horizontal",
  size = "md",
  className,
}: TemperatureGaugeProps) {
  const markerPct = clampMarkerPct(percentage);
  const isHorizontal = orientation === "horizontal";
  const thickness = SIZE_CLASS[orientation][size];
  const largeMarker = size === "lg" || size === "xl";

  const track = (
    // inset-x/y (not inset-0): the Tailwind 3 TV bundle emits `inset`, which older signage browsers skip.
    <div className="absolute inset-x-0 inset-y-0 overflow-hidden rounded-full bg-[var(--color-surface-muted)]" />
  );

  const fill = (
    <div
      className={cn(
        "absolute overflow-hidden transition-all duration-700 ease-out",
        isHorizontal ? "inset-y-0 left-0 rounded-l-full" : "inset-x-0 bottom-0 rounded-b-full",
      )}
      style={isHorizontal ? { width: `${markerPct}%` } : { height: `${markerPct}%` }}
    >
      <div
        className={cn("flex", isHorizontal ? "h-full" : "h-full w-full flex-col-reverse")}
        style={
          isHorizontal
            ? { width: `${10000 / Math.max(markerPct, 0.01)}%` }
            : { height: `${10000 / Math.max(markerPct, 0.01)}%` }
        }
      >
        <div
          className={
            isHorizontal ? "h-full bg-[var(--color-danger)]" : "w-full bg-[var(--color-danger)]"
          }
          style={isHorizontal ? { width: `${RED_PCT}%` } : { height: `${RED_PCT}%` }}
        />
        <div
          className={
            isHorizontal
              ? "h-full bg-[var(--color-status-yellow)]"
              : "w-full bg-[var(--color-status-yellow)]"
          }
          style={isHorizontal ? { width: `${YELLOW_PCT}%` } : { height: `${YELLOW_PCT}%` }}
        />
        <div
          className={
            isHorizontal ? "h-full bg-[var(--color-success)]" : "w-full bg-[var(--color-success)]"
          }
          style={isHorizontal ? { width: `${GREEN_PCT}%` } : { height: `${GREEN_PCT}%` }}
        />
      </div>
    </div>
  );

  const marker = (
    <>
      <div
        className={cn(
          // opacity-80 (not bg-.../80): Tailwind 3 cannot apply an alpha modifier to a var() color.
          "absolute rounded-full bg-[var(--color-text)] opacity-80 transition-all duration-700 ease-out",
          isHorizontal
            ? largeMarker
              ? "-bottom-1.5 -top-1.5 w-[3px]"
              : "-bottom-1 -top-1 w-[2px]"
            : largeMarker
              ? "-left-1.5 -right-1.5 h-[3px]"
              : "-left-1 -right-1 h-[2px]",
        )}
        style={
          isHorizontal
            ? { left: `${markerPct}%`, transform: "translateX(-50%)" }
            : { bottom: `${markerPct}%`, transform: "translateY(50%)" }
        }
      />
      <div
        className={cn(
          "absolute rounded-full border-2 border-white bg-[var(--color-text)] shadow-sm transition-all duration-700 ease-out",
          isHorizontal
            ? largeMarker
              ? "-top-[11px] h-3 w-3"
              : "-top-[9px] h-2.5 w-2.5"
            : largeMarker
              ? "-right-[11px] h-3 w-3"
              : "-right-[9px] h-2.5 w-2.5",
        )}
        style={
          isHorizontal
            ? { left: `${markerPct}%`, transform: "translateX(-50%)" }
            : { bottom: `${markerPct}%`, transform: "translateY(50%)" }
        }
      />
    </>
  );

  return (
    <div
      className={cn(
        "relative",
        isHorizontal ? cn("w-full", thickness) : cn("h-full", thickness),
        className,
      )}
    >
      {track}
      {markerPct > 0 ? fill : null}
      {marker}
    </div>
  );
}

export function gaugeStatusColor(percentage: number): string {
  if (percentage >= THRESHOLDS.green) return "text-[var(--color-success)]";
  if (percentage >= THRESHOLDS.yellow) return "text-[var(--color-status-yellow)]";
  return "text-[var(--color-danger)]";
}

export function gaugePercentage(actual: number, target: number | null | undefined): number {
  if (target == null || target <= 0) return actual > 0 ? 100 : 0;
  return Math.round((actual / target) * 100);
}
